<template>
  <div class="container trans-effect no-embed" :class="[isEmbedMode ? 'embed-container' : '']">
    <div class="top-area">
      <div class="top-area-right top-last">
        <div class="icon-cal" :class="isEmbed ? 'no-right-mg' : ''">
          <b-datepicker
            :week1="[dates.startDate, dates.endDate]"
            :week2="[dates.compareStartDate, dates.compareEndDate]"
            :hide-compare-with="true"
            @change="bDatepickerChanges"
          />
        </div>
      </div>
    </div>

    <div class="thw_content">
      <div class="internal-sub-nav">
        <div
          v-for="(tab, i) in tabOptions"
          :key="i"
          class="isn-item"
          :class="{ active: tab.id === selectedTab }"
          @click="tabOptions.length === 1 ? null : onTabClick(tab)"
        >
          {{ tab.value }}
        </div>
      </div>
    </div>

    <div v-if="tableauJwtToken" class="bdc_top_panel_wrap">
      <tableau-viz
        id="tableauViz"
        ref="tableauContainer"
        :src="tableauViewURL"
        :token="tableauJwtToken"
        :on-viz-load-error="handleTableauError"
        toolbar="top"
        hide-tabs
      >
        <custom-parameter name=":showShareOptions" value="false"></custom-parameter>
        <custom-parameter name=":customViews" value="false"></custom-parameter>

        <viz-filter field="Advertiser" :value="dspAdvertiserIds"> </viz-filter>
        <viz-filter field="Start Date" :value="startDate"></viz-filter>
        <viz-filter field="End Date" :value="endDate"></viz-filter>

        <viz-filter field=":refresh" value="yes"></viz-filter>
        <viz-parameter field="embed" value="yes"></viz-parameter>
      </tableau-viz>
    </div>
    <div v-else-if="!tableauJwtToken && !loading">
      Something went wrong. Please contact Digital Remedy technical support.
    </div>
  </div>
</template>

<script>
import _ from 'underscore';
import { get } from 'vuex-pathify';
import { TableauEventType } from '@tableau/embedding-api';
import {
  DATEPICKER_RANGE_OPTIONS,
  DATEPICKER_COMPARE_RANGE_OPTIONS,
  REACH_AND_FREQUENCY_TAB_OPTIONS,
} from '~/constant';
import { formatDateForAPI } from '~/util/apiDateFormat';
import gaEventsMixin from '~/components/mixins/ga-events-mixin';
import config from '~/config';
import {
  generateFlipDefaultDates,
  convertEpochToNYTimezone,
  isDemoInstance,
} from '~/util/utility-functions';
import tableauMixin from '~/components/mixins/tableau-mixin';

export default {
  components: {
    BDatepicker: () =>
      import(/* webpackChunkName: "b-datepicker" */ '~/components/elements/b-datepicker.vue'),
  },

  mixins: [gaEventsMixin, tableauMixin],

  data() {
    const tabOptions = REACH_AND_FREQUENCY_TAB_OPTIONS;
    let selectedTab = '';
    let selectedTabTitle = '';
    let selectedTabUrl = '';

    if (tabOptions?.length) {
      const defaultSelectedTab = tabOptions.find((t) => t.selected) || tabOptions[0];

      selectedTab = defaultSelectedTab?.id || tabOptions[0].id;
      selectedTabTitle = defaultSelectedTab?.value || tabOptions[0].value;
      if (isDemoInstance()) {
        selectedTabUrl = defaultSelectedTab?.demoViewUrl || tabOptions[0].demoViewUrl;
      } else {
        selectedTabUrl = defaultSelectedTab?.viewUrl || tabOptions[0].viewUrl;
      }
    }

    return {
      tabOptions,
      selectedTab,
      selectedTabTitle,
      selectedTabUrl,
      tableauJwtToken: null,
      tableauError: false,
      viz: null,
      loading: false,
      dspAdvertiserIds: [],
    };
  },

  computed: {
    advertiser: get('common/advertiser'),
    universalPixelId: get('common/universalPixelId'),
    isEmbedMode: get('common/isEmbedMode'),
    dates: get('dashboard/dates'),
    isEmbed() {
      return true;
    },
    startDate() {
      return formatDateForAPI(this.dates.startDate);
    },
    endDate() {
      return formatDateForAPI(this.dates.endDate);
    },
    tableauViewURL() {
      return `${config.TABLEAU_URL}${this.selectedTabUrl}`;
    },
  },

  watch: {
    dates: {
      immediate: false,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          await this.debounceTableauAuthToken();
        }
      },
    },
    tableauJwtToken: {
      deep: true,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          const tableauComponent = document.getElementById('tableauViz');
          if (!o && !this.viz) {
            await this.initializeTableau(tableauComponent);
          }
          if (this.viz) {
            this.viz.addEventListener(TableauEventType.VizLoadError, this.handleTableauError);
          }
        }
      },
    },
  },

  async mounted() {
    const storedDate = sessionStorage.getItem('selectedDates');
    if (storedDate !== undefined && storedDate !== null) {
      this.$store.set('dashboard/dates', JSON.parse(storedDate));
    } else {
      let computedDate = await get('dashboard/dates');
      if (isDemoInstance()) {
        const { startDate, endDate, dateRangeOption } = this.dates;
        computedDate = { startDate, endDate, dateRangeOption };
        this.$store.set('dashboard/dates', {
          ...computedDate,
        });
      } else {
        const { startDate, endDate, dateRangeOption, compareWith } = generateFlipDefaultDates();
        computedDate = { ...this.dates, startDate, endDate, dateRangeOption, compareWith };
        this.$store.set('dashboard/dates', {
          ...this.dates,
          ...computedDate,
        });
      }
    }

    this.loading = true;
    await this.fetchDsp();
    await this.initTableau(false);
  },

  methods: {
    async bDatepickerChanges(data) {
      this.$store.set(`common/RESET_CONVERSION_WINDOW`);
      const datePickerPayload = {
        startDate: convertEpochToNYTimezone(data.dates[0]),
        endDate: convertEpochToNYTimezone(data.dates[1].toDateString()),
        compareWith: data.compareWith,
        compareStartDate: convertEpochToNYTimezone(data.dates[2]),
        compareEndDate: convertEpochToNYTimezone(data.dates[3]),
        dateRangeOption: data.dateRangeOption,
        dateCompareOption: data.dateCompareOption,
      };
      this.$store.set('dashboard/dates', datePickerPayload);
      sessionStorage.setItem('selectedDates', JSON.stringify(datePickerPayload));
      this.datePickerChangedGAEvent(datePickerPayload);
      this.$store.dispatch('common/setIsWatchStats', {
        IMPRESSION: true,
        REACH: true,
        CONVERSION: true,
        LIFT: true,
      });
      this.$forceUpdate();
    },
    onTabClick(tab) {
      this.selectedTab = tab.id;
      this.selectedTabHandlerType = tab.handler;
      this.selectedTabTitle = tab.value;
      if (isDemoInstance()) {
        this.selectedTabUrl = tab.demoViewUrl;
      } else {
        this.selectedTabUrl = tab.viewUrl;
      }
      this.selectedTabSubUrl = tab.viewSubUrl;
      this.$store.set('dashboard/selectedDashboardTab', tab.id);
      this.debounceTableauAuthToken();
    },
    datePickerChangedGAEvent(datePickerPayload) {
      if (config.gaEnabled()) {
        const {
          startDate,
          endDate,
          compareWith,
          compareStartDate,
          compareEndDate,
          dateRangeOption,
          dateCompareOption,
        } = datePickerPayload;

        const gaEv = {
          start_date: startDate,
          end_date: endDate,
          compare: compareWith,
          date_range_option: DATEPICKER_RANGE_OPTIONS[`${dateRangeOption}`],
        };

        if (compareWith) {
          gaEv.compare_start_date = compareStartDate;
          gaEv.compare_end_date = compareEndDate;
          gaEv.compare_date_option = DATEPICKER_COMPARE_RANGE_OPTIONS[`${dateCompareOption}`];
        }
        this.fireGAEvent('flp_dtpk_change', gaEv);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@media screen {
  .dashboard-page-loader {
    position: relative;
    top: 250px;
  }
  * {
    box-sizing: border-box;
  }
  * {
    outline: 0;
  }
  .filter-details {
    display: inline-block;
    margin-right: 0.2rem;
    cursor: pointer;
    // &:hover {
    //   background-color: rgba(0, 0, 0, 0.2);
    // }

    .filter {
      display: inline-block;
      padding: 0 4px 0 4px;
      margin-right: 8px;
      text-align: left;

      &:last-child {
        margin-left: 10px;
        border-right: 1px dashed #232222;
        div {
          margin-right: 5px;
        }
      }

      div {
        font-size: 12px;

        &.main {
          padding: 2px 0 2px 0;
          font-weight: 500;
          color: #4c5461;
        }

        &.sub {
          font-weight: 600;
          color: #667184;
        }
      }
    }
  }
  .filter-details.lookup {
    position: relative;
    display: inline-block;
    padding: 8px;
    color: #717482;
    vertical-align: text-bottom;
    cursor: pointer;
    span.main {
      font-size: 11px;
      font-weight: 300;
      color: #717482;
      span.sub {
        display: inline-block;
        width: 70px;
        margin-top: -5px;
        overflow: hidden;
        font-size: 12px;
        font-weight: 400;
        color: #a1a5af;
        text-align: right;
        text-overflow: ellipsis;
        white-space: nowrap;
        vertical-align: top;
        cursor: pointer;
      }
      svg {
        position: relative;
        display: inline-flex;
        align-items: center;
        font-size: 10px;
        color: #ccc;
      }
    }
  }
  .light-theme {
    .top-export-icon {
      color: #444;
    }
  }
  .top-export-icon {
    position: relative;
    display: inline-block;
    margin-top: 10px;
    margin-right: 30px;
    font-size: 21px;
    color: #ddd;
    vertical-align: top;
  }
  .top-export-icon.downloadIcon {
    margin: 12px 5px 0px 10px;
  }

  .top-export-icon:hover {
    color: var(--primarycolor);
  }
}

@media screen {
  * {
    box-sizing: border-box;
  }
  .light-theme .flp-panel {
    color: #212325;
    box-shadow: 0px 14px 35px -12px rgba(0, 40, 100, 0.17);
    background-color: #fff;
    border: 1px solid rgb(165 173 178 / 22%);
    border-radius: 8px;
  }
  .flp-panel {
    position: relative;
    display: inline-block;
    padding: 30px;
    color: #fff;
    background-color: #2f323a;
    border-radius: 3px;
    -webkit-transition: background-color 0.3s ease;
    -moz-transition: background-color 0.3s ease;
    -ms-transition: background-color 0.3s ease;
    -o-transition: background-color 0.3s ease;
    transition: background-color 0.3s ease;
  }
  .flp-panel-title {
    display: inline-block;
    font-size: 15px;
    font-weight: 400;
    line-height: 1.1em;
  }
  .input1 {
    position: relative;
    display: inline-block;
    width: 200px;
    min-width: 150px;
    height: 100%;
    padding: 10px;
    font-size: 15px;
    text-align: left;
    border: 0;
    border-radius: 4px;
  }
  .inputdark {
    color: #fff;
    background-color: #262933;
  }

  .wrap4icon.icon-cal {
    position: relative;
    display: inline-block;
  }
  * {
    outline: 0;
  }
  .filter-panel-wrap {
    align-items: center;
    margin-bottom: 0;
    color: #cad1d6;
    border-top: 1px solid #212429;
    transition: left 0.3s ease;
    .fp-text-link {
      font-size: 12px;
      color: #707e8a;
      display: inline-block;
      margin-top: 15px;
      display: flex;
      align-items: center;
      & p {
        text-decoration: underline;
        cursor: pointer;
      }
      & p:hover {
        color: var(--primarycolor) !important;
      }
      & span {
        text-decoration: none;
        margin-left: 8px;
        color: #cad1d6;
        .advance-filter-clear-icon:hover {
          color: var(--primarycolor) !important;
        }
      }
      & span:hover {
        color: #cad1d6;
      }
    }
  }
  .exportable-table-wrap {
    min-height: 20rem;
    max-height: 30rem;
    margin-bottom: 35px;
    overflow: hidden;
    transition: all 0.3s ease;
  }
  .ex-close {
    padding: 9px 2px 9px 20px;
  }
  .ex-close:hover {
    color: var(--primarycolor);
  }
  .flp-panel-top {
    padding-bottom: 20px;
    margin-bottom: 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
  .fright {
    position: absolute;
    right: 0px;
    display: flex;
    width: 240px;
    margin-top: -1%;
  }
  .overflowshow {
    overflow: visible;
  }
  .intip4 {
    position: relative;
    top: 0;
  }
  .intip4[data-tool-tip]::after {
    position: absolute;
    bottom: 100%;
    left: 0;
    z-index: 99;
    display: block;
    width: 100px;
    padding: 10px 12px;
    margin-left: -50px;
    font-size: 12px;
    line-height: 1.2em;
    color: #5b5b5b;
    text-align: center;
    pointer-events: none;
    content: attr(data-tool-tip);
    background-color: #fdea85;
    border-radius: 3px;
    transition: ease 0.3s;
    transform: scale(0);
    transform-origin: bottom center;
  }
  .intip4[data-tool-tip]::after {
    width: 155px;
    margin-left: -70px;
    font-size: 12px;
    font-weight: 400;
    color: #222;
    pointer-events: none;
    background-color: rgba(245, 245, 250, 0.9);
  }
  .intip4[data-tool-tip]:hover::after {
    bottom: -145%;
    transform: scale(1);
  }
  .campaign-cnt {
    display: inline-block;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2em;
    color: #646c86;
    letter-spacing: 0.02em;
  }
}
::v-deep .media-settings {
  .filter-group > *:not(:first-child) {
    margin-top: 12px !important;
  }
  .media-select {
    // width: 180px !important;
    // min-width: 180px !important;
  }
  .dropdown {
    width: 180px !important;
    min-width: 180px !important;
    margin: 0 0 0 0 !important;
    &:not(:first-child) {
      // margin-left: 15px !important;
    }
  }
}
::v-deep .method-settings {
  .dropdown {
    width: 198px !important;
    min-width: 198px !important;
    &:not(:first-child) {
      // margin-left: 15px !important;
    }
  }
  .filter-group {
    .tot-uniq,
    .conv-content {
      // margin-left: 15px !important;
      .conversion-opt-cont {
        display: flex;
      }
    }
  }
}
.light-theme {
  .filter-panel-wrap {
    .filter {
      .filter-group {
        .flp-input {
          color: #212325;
          background-color: #fff;
          border: 1px solid #c5d2d8;
        }
      }
      ::v-deep .dropdown .dropdown-menu {
        li svg {
          color: #555 !important;
          &.check-off {
            color: transparent !important;
          }
        }
      }
    }
  }
  .no-right-mg {
    right: -11px !important;
  }
}
::v-deep .filter-panel-wrap .filter.media-settings .dropdown .dropdown-menu {
  max-height: 300px !important;
  overflow: hidden !important;
  overflow-y: auto !important;
  li svg {
    font-size: 17px !important;
    box-shadow: inset 0px 0px 1px 1px #454546;
    color: #fff;
  }
}
.dropdown.events-dd {
  width: 192px !important;
  margin-right: 10px !important;
}
::v-deep .dropdown.events-dd .select {
  min-height: auto !important;
}
.inl-blk {
  display: inline-block;
}
.margin-btm-10 {
  margin-bottom: 10px;
}

.vue-daterange-picker {
  color: black !important;
}

.expand-table-enter-active,
.expand-table-leave-active {
  // -webkit-transition: left 0.3s ease, opacity 0.3s ease;
  // -moz-transition: left 0.3s ease, opacity 0.3s ease;
  // -ms-transition: left 0.3s ease, opacity 0.3s ease;
  // -o-transition: left 0.3s ease, opacity 0.3s ease;
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
}

.expand-table-enter,
.expand-table-leave-to {
  transform: translateX(-100%) !important;
  opacity: 0 !important;
}

// Top Area CSS - START

.top-area {
  margin-bottom: 15px;
  color: #fff;
  margin-top: 15px;
}
.logo {
  display: inline-block;
  width: 250px;
  height: auto;
}
.logo img {
  width: 100%;
  height: auto;
}
.top-area-center {
  position: relative;
  top: 0;
  left: -125px;
  display: flex;
  margin: 0;
}
.top-area-center-checkbox {
  position: relative;
  display: inline-block;
  margin-left: 40px;
}
label {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
input[type='checkbox'] {
  display: none;
}
input[type='checkbox'] + label:before {
  display: inline-block;
  font-family: 'FontAwesome';
}
input[type='checkbox'] + label:after {
  display: inline-block;
  font-family: 'FontAwesome';
}
input[type='checkbox'] + label:before {
  position: relative;
  top: -2px;
  left: 2px;
  z-index: 2;
  font-size: 14px;
  font-weight: 600;
  color: var(--primarycolor);
  letter-spacing: 10px;
  content: '\f00c';
  opacity: 0;
}
input[type='checkbox']:checked + label:before {
  font-weight: 600;
  letter-spacing: 10px;
  content: '\f00c';
  opacity: 1;
}
input[type='checkbox'] + label::after {
  position: absolute;
  left: 0px;
  font-size: 18px;
  font-weight: 500;
  color: rgba(150, 150, 150, 0.4);
  content: '\f1db';
}
input[type='checkbox']:checked + label::after {
  color: var(--primarycolor);
}
.top-area-right {
  position: inherit;
  display: inline-block;
  // float: right;
  // width: 972px;
  width: 100%;
  min-height: 50px;
  // overflow-x: scroll;
  // text-align: right;
  white-space: nowrap;
}
.input-value {
  position: relative;
  display: inline-block;
  width: 200px;
  min-width: 150px;
  height: 100%;
  padding: 10px 10px;
  font-size: 15px;
  text-align: left;
  border: 0px;
  border-radius: 4px;
}
.icon-cal {
  position: relative;
  top: -6px;
  display: inline-flex;
  float: right;
  vertical-align: top;
  right: 20px;
}
::v-deep .icon-cal .wrap svg {
  position: absolute;
  top: 50%;
  right: 16px;
  z-index: 1;
  margin-top: -11px;
  pointer-events: none;
}
.intdark {
  color: #fff;
  background-color: #262933;
}
// Top Area CSS - END

// Last Area CSS - START

.last-area {
  margin-top: 60px;
}

.nav-tabs {
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: 1;
  display: inline-block;
  margin: -33px 0px 0px 0;
}
.light-theme .left-part {
  background-color: #fff;
  color: #212325;
  box-shadow: 0px 14px 35px -12px rgba(0, 40, 100, 0.17);
  border: 1px solid rgb(165 173 178 / 22%);
  border-radius: 8px;
}

.left-part {
  position: relative;
  z-index: 2;
  float: left;
  width: 30%;
  min-width: 366px;
  min-height: 585px;
  padding: 30px 0px;
  color: #fff;
  background-color: #212429;
  border-radius: 0px;
}
.filter-group.mediaFilters {
  display: flex;
  flex-direction: column;
}
.light-theme .right-part {
  color: #212325;
  box-shadow: 0px 14px 35px -12px rgba(0, 40, 100, 0.17);
  background-color: #fff;
  border: 1px solid rgb(165 173 178 / 22%);
  border-radius: 8px;
}
.right-part {
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  float: left;
  width: 70%;
  min-width: 648px;
  min-height: 585px;
  padding: 30px 20px;
  margin-left: 25px;
  color: #fff;
  background-color: #212429;
  border-radius: 0px;
}

// Last Area CSS - END

// Download Icon CSS - START

svg:not(:root).svg-inline--fa {
  overflow: visible;
}
.svg-inline--fa {
  display: inline-block;
  height: 1em;
  overflow: visible;
  font-size: inherit;
  vertical-align: -0.125em;
}
.svg-inline--fa.fa-w-12 {
  width: 0.75em;
}

.fa-arrow-to-bottom:before {
  content: '\f33d';
}

// Download Icon CSS - END

// Export Table - START

svg:not(:root).svg-inline--fa {
  overflow: visible;
}
.svg-inline--fa {
  display: inline-block;
  height: 1em;
  overflow: visible;
  font-size: inherit;
  vertical-align: -0.125em;
}
.svg-inline--fa.fa-w-10 {
  width: 0.625em;
}

.fa-times:before {
  content: '\f00d';
}

.fa-times:before {
  content: '\f00d';
}
.top-last {
  margin-top: 0px;
}

.container-chart {
  position: absolute;
  top: 120px;
  width: 100%;
  height: 100%;
}

.chart-filters {
  display: flex;
  .chart-widget {
    position: absolute;
    right: 55px;
  }
  .flp-opt-panel {
    top: 40px;
    right: 30px;
  }
}
::v-deep .dropdown {
  margin-right: 0 !important;
}

::v-deep .chart-filters .dropdown .dropdown-menu li {
  font-size: 14px;
}
::v-deep .exportable-table-wrap .flp-panel-top .form-input-wrap {
  margin-top: -1% !important;
}
::v-deep .form-input-field {
  background-color: #262933;
  border: none;
  border-radius: 4px;
}
::v-deep .form-input-field.v-select .vs__dropdown-toggle .vs__actions .action-icons svg {
  color: inherit !important;
}
::v-deep .form-input-wrap .form-click .action-icons {
  padding: 0 11px 0 0 !important;
}
::v-deep .form-input-wrap .form-click {
  min-height: 40px;
}
::v-deep .form-input.form-date svg {
  left: 90%;
  z-index: 1;
}
.exportable-table-wrap .flp-panel-top {
  display: flex;
}
.exportable-table-wrap .dboard-select {
  width: 12rem;
  min-width: 150px;
  margin-top: -10px;
  margin-left: 1rem;
  background-color: #474a56;
  border-radius: 4px;
}
.exportable-table-wrap .flp-panel-top .flp-panel-title {
  width: 175px;
}
.exportable-table-wrap .flp-controls {
  display: flex;
  width: 100%;
}
.dboard-select {
  width: 12rem;
  min-width: 182px;
  margin-top: 4px;
  margin-right: 1.2rem;
  margin-left: 0;
  background-color: #474a56;
  border-radius: 4px;
}
::v-deep .icon-cal .form-input-field {
  width: 190px;
  margin: 4px 0 0 0;
  cursor: pointer;
}
::v-deep .k-btn {
  display: inline-block;
  min-width: 75px;
  font-weight: 600;
  color: var(--buttontext) !important;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  background-color: var(--primarycolor);
  border-radius: 3px;
  appearance: none;
}
::v-deep .k-btn.btn-size-3 span.label {
  padding: 9px 22px;
  line-height: 20px;
}
::v-deep .k-btn:hover {
  color: #cad1d6 !important;
  background-color: #424242 !important;
}
.light-theme {
  .filter-panel-wrap {
    background-color: #fbfcfe;
    box-shadow: 10px 20px 20px rgb(63 66 77 / 16%);
    color: #212325;
    border-right: 1px solid #f2f6ff;
    border-top: unset;
    svg {
      color: #212325 !important;
    }
    .fp-text-link {
      p {
        color: #707e8a !important;
      }
      span {
        color: #212325 !important;
      }
    }
  }
}
.filter-panel-wrap {
  position: fixed;
  top: 55px;
  bottom: 0;
  left: 0;
  z-index: 3;
  display: block;
  width: auto;
  min-width: 360px;
  height: 94vh;
  padding: 30px 80px 20px;
  background: #121215;
  background-color: rgb(16 16 19 / 93%);
  border-right: 1px solid #1d1f23;
  box-shadow: inset -10px 10px 20px rgb(0 0 0 / 30%);
  box-shadow: 10px 30px 30px rgb(0 0 0 / 50%);
  -webkit-transition: all 0.3s ease;
  -moz-transition: all 0.3s ease;
  -ms-transition: all 0.3s ease;
  -o-transition: all 0.3s ease;
  transition: all 0.3s ease;
  backdrop-filter: saturate(140%) blur(5px);
  span {
    margin-top: 2px;
  }
  svg {
    color: #cad1d6 !important;
  }
  .filter {
    ::v-deep .dropdown .dropdown-menu {
      overflow: unset !important;
      overflow-y: unset !important;
      li svg {
        font-size: inherit !important;
      }
      .intip4 {
        top: 2px !important;
        margin-left: 5px !important;
        &::after {
          bottom: 200% !important;
          width: 198px !important;
          margin-left: -73px !important;
          font-weight: 400 !important;
          text-align: left !important;
          letter-spacing: 0em !important;
        }
      }
    }
    .filter-ttl {
      margin-bottom: 10px;
      font-size: 13px;
      font-weight: 400;
      line-height: 1.1em;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .filter-group > *:not(:first-child) {
      margin-top: 10px;
    }

    .filter-group {
      margin-bottom: 30px;
      .conv-content {
        position: relative;
        margin-top: 30px;
        // display: inline-block;

        .conv-ttl {
          position: absolute;
          top: -80%;
          font-size: 14px;
          color: #707e8a;
        }
      }

      .opt-b1 {
        display: inline-block;
        margin-right: 6px;
      }
      .opt-b2 {
        display: inline-block;
        width: 5rem;
        margin-left: 70px;
        vertical-align: bottom;
        position: fixed;
        & .checkbox-btn {
          height: unset;
        }
        ::v-deep .checkbox-btn {
          span {
            position: fixed;
            margin: 0 30px !important;
            font-size: 12px !important;
          }
        }
      }

      .input-conv {
        width: 60px;
      }

      .flp-input {
        padding: 10px;
        color: #ddd;
        background-color: transparent;
        border: 1px solid rgba(170, 170, 170, 0.2);
        border-radius: 4px;
      }
    }
  }

  .close-filters {
    position: absolute;
    top: 10px;
    right: 0;
    margin-right: 20px;
    cursor: pointer;
    &:hover {
      color: var(--primarycolor) !important;
    }
  }
}
.no-display {
  display: none !important;
}

.sideopen {
  position: relative;
  left: 12%;
  width: calc(97% - 110px);
  .filter-panel-wrap {
    left: 0px;
  }
}
.no-embed {
  padding-left: 50;
}
.trans-effect {
  -webkit-transition: all 0.3s ease;
  -moz-transition: all 0.3s ease;
  -ms-transition: all 0.3s ease;
  -o-transition: all 0.3s ease;
  transition: all 0.3s ease;
}

// end of new header
::v-deep .modal-mask {
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999 !important;
  background-color: rgba(0, 0, 0, 0.8) !important;
  .modal-wrapper {
    max-width: initial;
    max-height: initial;
    overflow: hidden !important;
    .modal-container {
      background-color: transparent;
      padding: 0;
      min-width: 1250px;
      .exportable-table-wrap {
        max-height: initial;
      }
      .table-scroll {
        max-height: initial;
        height: 60vh;
      }
      .table-no-data {
        height: 60vh;
      }
      .table-loader {
        height: 60vh;
      }
    }
  }
}
.filter-cnt-bubble {
  background-color: var(--primaryalt);
}

.filter-cnt-badge {
  white-space: nowrap;
  text-align: center;
  line-height: 18px;
  padding: 0 6px;
  height: 18px;
  background: var(--primarycolor);
  position: absolute;
  border-radius: 35px;
  font-size: 12px;
  color: var(--buttontext);
  left: 13px;
  top: -5px;
  font-weight: 600;
}

.filter-cnt-badge-cmpgn {
  white-space: nowrap;
  text-align: center;
  line-height: 18px;
  padding: 0 6px;
  height: 18px;
  background: var(--primarycolor);
  position: absolute;
  border-radius: 35px;
  font-size: 12px;
  color: var(--buttontext);
  left: 55px;
  top: -3px;
  font-weight: 600;
}

.filter-cnt-badge-adg {
  white-space: nowrap;
  text-align: center;
  line-height: 18px;
  padding: 0 6px;
  height: 18px;
  background: var(--primarycolor);
  position: absolute;
  border-radius: 35px;
  font-size: 12px;
  color: var(--buttontext);
  left: 51px;
  top: -3px;
  font-weight: 600;
}
</style>
<style lang="scss">
@media (max-width: 480px) {
  .image img {
    position: absolute;
    top: 19%;
    left: 21%;
  }
}

.daterangepicker.opensright:before {
  left: 360px !important;
}
.daterangepicker.opensright:after {
  left: 361px !important;
}
.daterangepicker {
  .drp-buttons {
    .btn {
      text-transform: uppercase;
    }
    .applyBtn {
      color: #fff !important;
      background-color: var(--primarycolor) !important;
    }
  }
}
.daterangepicker .drp-buttons .applyBtn:hover,
.daterangepicker .drp-buttons .cancelBtn:hover {
  background-color: #424242 !important;
}
::-webkit-scrollbar {
  width: 5px;
  height: 7px;
}
::-webkit-scrollbar-button {
  width: 0px;
  height: 0px;
}
::-webkit-scrollbar-thumb {
  background: rgba(111, 111, 111, 0.2);
  border: 0px none #ffffff;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: #525965;
}
::-webkit-scrollbar-thumb:active {
  background: #525965;
}
::-webkit-scrollbar-track {
  background: transparent;
  border: 0px none #ffffff;
  border-radius: 50px;
}
::-webkit-scrollbar-track:hover {
  background: transparent;
}
::-webkit-scrollbar-track:active {
  background: transparent;
}
::-webkit-scrollbar-corner {
  background: transparent;
}

.checkbox-btn span {
  margin: 0 40px 0 10px !important;
  font-size: 14px !important;
}

.ddselect {
  width: 200px;
  height: 40px;
  margin-left: 1rem;
  background-color: #474a56;
  border-radius: 4px;
}
.container-chart {
  text-align: center;
  svg {
    display: block;
    margin: auto;
  }
}
.c3 text.c3-empty {
  font-size: 13px;
  fill: #bac0c5 !important;
}
.light-theme {
  .txt-center {
    color: #444;
  }
}
.txt-center {
  position: absolute;
  width: 740px;
  font-size: 13px;
  color: #bac0c5;
}
.txt-align-center {
  display: inline-grid;
  align-items: center;
  width: 100%;
  min-height: 65px;
  font-size: 13px;
  color: #bac0c5;
  text-align: center;
}
.showIncrBox {
  .mid-area-1 {
    //width: 25% !important;
    padding: 20px 15px !important;
    .stat-cnt {
      width: 100%;
      .mid-area-1-cont {
        margin-right: 0.5rem !important;
        width: auto !important;
        span.big {
          font-size: 36px !important;
        }
      }
    }
  }
  .quick-stat:not(:last-child) {
    margin: 0 20px 0 0 !important;
  }
}
.disableFilterPanel {
  pointer-events: none !important;
  opacity: 0.3;
  filter: brightness(0.9);
}
.intermediate-screen-bg {
  background-color: #fff;
  height: calc(100vh - 60px);
}
.readonly-db {
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.4;
}
.advance-filter-clear-icon {
  position: relative;
  top: -2px;
  left: 5px;
  cursor: pointer;
  font-size: 13px;
  &:hover {
    color: var(--primarycolor) !important;
  }
}
.dashboard-loading-spinner {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.drp-wrap {
  position: relative;
  display: block;
  padding: unset;
  margin-right: unset;
  font-size: 14px;
  line-height: 1.42857143;
  color: #fff;
  background-color: #2d3138;
  background-image: none;
  border-bottom: 0px;
  border-radius: 4px;
  -webkit-box-shadow: none !important;
  box-shadow: none !important;
  font-family: sans-serif;
}
.light-theme .drp-wrap {
  background-color: #f9f9f9;
  border: none;
  color: #2f323a;
  border-bottom: 1px;
  text-align: right;
  padding: 6px 12px;
  font-size: 14px;
  > input {
    color: #2f323a !important;
    font-family: Arial, sans-serif;
  }
  .compare-label {
    color: rgba(129, 140, 165, 1);
  }
}
.light-theme .rpt-bldr {
  font-size: 13px !important;
  padding: 9px 0px 9px 7px !important;
}
.drp-wrap > input {
  width: 100%;
  min-width: 220px;
  max-width: unset;
  height: auto !important;
  padding: 0 !important;
  margin-bottom: 0;
  font-size: 1.13em;
  line-height: normal;
  color: #fff;
  letter-spacing: 0.03em;
  cursor: pointer;
  background: none !important;
  border: none;
  outline: none;
  box-shadow: none !important;
  font-family: sans-serif;
}

.controls .drp-wrap > input {
  min-height: 27px;
}

.drp-wrap .compare-label {
  bottom: 0px;
  font-size: 75%;
  color: rgba(129, 140, 165, 1);
  text-align: left;
  cursor: pointer;
}

.drp-wrap .compare-label > span {
  margin-left: 5px;

  /* bottom: 0px; */

  /* font-size: 80%; */
  color: rgb(129 140 165);
  cursor: pointer;
}

.drp-wrap {
  background-color: transparent;
  border: 1px solid transparent;
}

.external-loading-dots {
  position: absolute;
  left: 55px;
  top: -3px;
  display: flex;
  align-items: center;
  font-size: 12px;
  color: var(--primarycolor);
}

.external-loading-dots .dot {
  background-color: currentColor;
  border-radius: 50%;
  width: 0.35em;
  height: 0.35em;
  margin: 0 0.15em;
  animation-name: pulseDotAnimation;
  animation-duration: 1.4s;
  animation-iteration-count: infinite;
  animation-timing-function: ease-in-out;
}

.external-loading-dots .dot:nth-child(1) {
  animation-delay: -0.32s;
}

.external-loading-dots .dot:nth-child(2) {
  animation-delay: -0.16s;
}

.external-loading-dots .dot:nth-child(3) {
  animation-delay: 0s;
}

@keyframes pulseDotAnimation {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.6;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
.campaign-section {
  margin-right: 25px;
}

.bdc_main_wrap.whero {
  margin-top: 19px;
  -webkit-transition: all 0.3s ease;
  -moz-transition: all 0.3s ease;
  -ms-transition: all 0.3s ease;
  -o-transition: all 0.3s ease;
  transition: all 0.3s ease;
}
.body-content.fullmax .thw_content {
  max-width: 100%;
}
.thw_content {
  max-width: 100%;
  margin: auto;
  -webkit-transition: all 0.3s ease;
  -moz-transition: all 0.3s ease;
  -ms-transition: all 0.3s ease;
  -o-transition: all 0.3s ease;
  transition: all 0.3s ease;
  padding: 0px;
  position: relative;
  z-index: 11;
}
.sopened .thw_content {
  max-width: 100%;
}
.internal-sub-nav {
  display: inline-block;
  float: none;
  margin: 11px 20px 31px 5px;
  display: block;
}
.isn-item {
  display: inline-block;
  margin-right: 20px;
  font-size: 14px;
  font-weight: 600;
  color: var(--primarydark2);
  position: relative;
  cursor: pointer;
}

.isn-item.active {
  color: var(--primarydark);
}

.isn-item.active::before {
  content: '';
  width: 100%;
  height: 2px;
  margin-top: 25px;
  position: absolute;
  background-color: var(--primarycolor);
}

.isn-item:hover {
  color: var(--primarydark);
}

.isn-item:hover::before {
  content: '';
  width: 100%;
  height: 2px;
  margin-top: 25px;
  position: absolute;
  background-color: var(--primarydark2);
}
@media (max-width: 750px) {
  .bdc_top_panel_wrap {
    flex-wrap: wrap !important;
  }
}
.bdc_top_panel_wrap {
  display: flex;
  justify-content: space-between;
  position: relative;
  top: 3;
}
.chart-sec {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
}
</style>
